import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useDonationData({ filters, pagination, setPagination, sortBy, sortOrder, debouncedSelectedIds }) {
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [matchedDonorNames, setMatchedDonorNames] = useState(null);
  const [topDonors, setTopDonors] = useState([]);
  const [topRecipients, setTopRecipients] = useState([]);

  // Fetch top donors and recipients list for navigation
  useEffect(() => {
    const fetchLists = async () => {
      try {
        const [donorsRes, recipientsRes] = await Promise.all([
          axios.get(`${API}/charts/top-donors?limit=50`),
          axios.get(`${API}/charts/top-recipients?limit=50`),
        ]);
        setTopDonors(donorsRes.data);
        setTopRecipients(recipientsRes.data);
      } catch (error) {
        console.error("Failed to fetch lists:", error);
      }
    };
    fetchLists();
  }, []);

  const fetchDonations = useCallback(async () => {
    if (!debouncedSelectedIds) {
      setDonations([]);
      setPagination(prev => ({ ...prev, total: 0, totalPages: 0 }));
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        page_size: pagination.pageSize.toString(),
        sort_by: sortBy,
        sort_order: sortOrder,
      });

      if (filters.year) params.append("year", filters.year);
      if (filters.donor && matchedDonorNames && matchedDonorNames.length > 1) {
        params.append("donor", matchedDonorNames.join("|"));
      } else if (filters.donor) {
        params.append("donor", filters.donor);
      }
      if (filters.recipient) params.append("recipient", filters.recipient);
      if (filters.address) params.append("address", filters.address);
      if (filters.minAmount) params.append("min_amount", filters.minAmount);
      if (filters.maxAmount) params.append("max_amount", filters.maxAmount);
      if (filters.amountType && filters.amountType !== "all") params.append("amount_type", filters.amountType);
      if (debouncedSelectedIds) params.append("dataset_ids", debouncedSelectedIds);

      const response = await axios.get(`${API}/donations?${params.toString()}`);
      setDonations(response.data.data);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
        totalPages: response.data.total_pages,
      }));
    } catch (error) {
      console.error("Failed to fetch donations:", error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, sortBy, sortOrder, filters, debouncedSelectedIds, matchedDonorNames, setPagination]);

  const fetchSummary = useCallback(async () => {
    if (!filters.donor && !filters.recipient) {
      setSummary(null);
      setMatchedDonorNames(null);
      return;
    }

    setSummaryLoading(true);
    try {
      let response;
      if (filters.donor) {
        response = await axios.get(`${API}/summary/donor/${encodeURIComponent(filters.donor)}`);
      } else if (filters.recipient) {
        response = await axios.get(`${API}/summary/recipient/${encodeURIComponent(filters.recipient)}`);
      }
      if (response?.data && !response.data.error) {
        setSummary({ ...response.data, type: filters.donor ? 'donor' : 'recipient' });
        if (response.data.matching_names && response.data.matching_names.length > 0) {
          setMatchedDonorNames(response.data.matching_names);
        } else {
          setMatchedDonorNames(null);
        }
      } else {
        setSummary(null);
        setMatchedDonorNames(null);
      }
    } catch (error) {
      console.error("Failed to fetch summary:", error);
      setSummary(null);
      setMatchedDonorNames(null);
    } finally {
      setSummaryLoading(false);
    }
  }, [filters.donor, filters.recipient]);

  useEffect(() => { fetchDonations(); }, [fetchDonations]);
  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  return {
    donations,
    loading,
    summary,
    summaryLoading,
    topDonors,
    topRecipients,
  };
}
